import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Check, Star, Lightning, Camera, Storefront, CalendarBlank, Ticket, ArrowRight, X, Gift, VideoCamera } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PricingPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [validating, setValidating] = useState(false);
  const [trialEligible, setTrialEligible] = useState(false);
  const [startingTrial, setStartingTrial] = useState(false);

  useEffect(() => {
    if (user && user.subscription !== 'premium') {
      axios.get(`${API}/payments/trial-eligible`, { withCredentials: true })
        .then(({ data }) => setTrialEligible(data.eligible))
        .catch(() => {});
    }
  }, [user]);

  const handleValidateCode = async () => {
    if (!discountCode.trim()) return;
    setValidating(true);
    try {
      const { data } = await axios.post(`${API}/discount/validate`, { code: discountCode.trim() });
      setAppliedDiscount(data);
      toast.success(`Code applied! ${data.percent_off}% off — ${data.description}`);
    } catch (err) {
      setAppliedDiscount(null);
      toast.error(err.response?.data?.detail || 'Invalid discount code');
    } finally {
      setValidating(false);
    }
  };

  const clearDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCode('');
  };

  const calcPrice = (base) => {
    if (!appliedDiscount) return base;
    return Math.round(base * (1 - appliedDiscount.percent_off / 100) * 100) / 100;
  };

  const handleCheckout = async (packageType) => {
    if (!user) { navigate('/login'); return; }
    try {
      const { data } = await axios.post(`${API}/payments/create-checkout`, {
        package_type: packageType,
        origin_url: window.location.origin,
        discount_code: appliedDiscount?.code || '',
      }, { withCredentials: true });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
    }
  };

  const handleStartTrial = async () => {
    if (!user) { navigate('/login'); return; }
    setStartingTrial(true);
    try {
      const { data } = await axios.post(`${API}/payments/start-free-trial`, {}, { withCredentials: true });
      toast.success(data.message);
      await refreshUser();
      setTrialEligible(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start free trial');
    } finally {
      setStartingTrial(false);
    }
  };

  const isPremium = user?.subscription === 'premium';

  const monthlyPrice = 50;
  const yearlyPrice = 480;
  const listingPrice = 3;

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#F9F9F6] px-4 py-12" data-testid="pricing-page">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#004E89] mb-3" style={{ fontFamily: 'Outfit' }}>
            Simple, Fair Pricing
          </h1>
          <p className="text-[#5C7A99] text-base md:text-lg max-w-md mx-auto">
            Pay per listing or go unlimited with our Pro plans
          </p>
        </div>

        {/* Free for Shoppers Banner */}
        <div className="max-w-2xl mx-auto mb-10" data-testid="free-shoppers-banner">
          <div className="bg-[#4CB944] bg-opacity-10 border-2 border-[#4CB944] rounded-2xl p-5 shadow-[4px_4px_0px_#4CB944] text-center">
            <h2 className="text-xl font-black text-[#004E89] mb-1" style={{ fontFamily: 'Outfit' }}>
              Shopping? It's Always FREE!
            </h2>
            <p className="text-sm text-[#5C7A99]">
              Browse sales, view details, save favorites, and get alerts — <strong className="text-[#4CB944]">100% free, forever.</strong> Pricing below is only for people posting sales.
            </p>
          </div>
        </div>

        {/* Discount Code Box */}
        <div className="max-w-lg mx-auto mb-10" data-testid="discount-code-section">
          <div className="bg-white border-2 border-[#004E89] rounded-2xl p-5 shadow-[4px_4px_0px_#004E89]">
            <div className="flex items-center gap-2 mb-3">
              <Ticket size={22} weight="duotone" className="text-[#FF6B35]" />
              <span className="text-sm font-bold text-[#004E89] uppercase tracking-wide" style={{ fontFamily: 'Outfit' }}>Have a discount code?</span>
            </div>
            {appliedDiscount ? (
              <div className="flex items-center justify-between bg-[#4CB944] bg-opacity-10 border-2 border-[#4CB944] rounded-xl px-4 py-3">
                <div className="flex items-center gap-2">
                  <Check size={18} weight="bold" className="text-[#4CB944]" />
                  <span className="text-sm font-bold text-[#004E89]">
                    {appliedDiscount.code} — {appliedDiscount.percent_off}% off
                  </span>
                </div>
                <button onClick={clearDiscount} className="text-[#5C7A99] hover:text-red-500 transition-colors" data-testid="clear-discount-btn">
                  <X size={18} weight="bold" />
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input
                  type="text"
                  value={discountCode}
                  onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                  className="input-neo flex-1 py-2.5 text-sm uppercase tracking-wider"
                  placeholder="Enter code..."
                  onKeyDown={(e) => e.key === 'Enter' && handleValidateCode()}
                  data-testid="discount-code-input"
                />
                <button
                  onClick={handleValidateCode}
                  disabled={validating || !discountCode.trim()}
                  className="btn-primary py-2 px-5 text-sm"
                  data-testid="apply-discount-btn"
                >
                  {validating ? '...' : 'Apply'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {/* Pay Per Listing */}
          <div className="pricing-card" data-testid="pricing-per-listing">
            <div className="w-12 h-12 rounded-xl bg-[#FFD166] border-2 border-[#004E89] flex items-center justify-center mb-4">
              <Storefront size={24} weight="duotone" className="text-[#004E89]" />
            </div>
            <h2 className="text-xl font-black text-[#004E89] mb-1" style={{ fontFamily: 'Outfit' }}>Per Listing</h2>
            <div className="flex items-baseline gap-1 mb-1">
              {appliedDiscount ? (
                <>
                  <span className="text-3xl font-black text-[#FF6B35]" style={{ fontFamily: 'Outfit' }}>${calcPrice(listingPrice).toFixed(2)}</span>
                  <span className="text-lg text-[#5C7A99] line-through font-medium">${listingPrice}</span>
                </>
              ) : (
                <span className="text-3xl font-black text-[#FF6B35]" style={{ fontFamily: 'Outfit' }}>${listingPrice}</span>
              )}
              <span className="text-[#5C7A99] font-medium text-sm">/listing</span>
            </div>
            {appliedDiscount && (
              <div className="text-xs font-bold text-[#4CB944] mb-2">{appliedDiscount.percent_off}% off applied</div>
            )}

            {/* First listing free banner */}
            <div className="bg-[#4CB944] bg-opacity-10 border-2 border-[#4CB944] rounded-xl p-3 mb-4 mt-3" data-testid="first-listing-free-banner">
              <div className="flex items-center gap-2">
                <Gift size={18} weight="fill" className="text-[#4CB944] flex-shrink-0" />
                <span className="text-xs font-bold text-[#004E89]">First listing is FREE for new users!</span>
              </div>
            </div>

            <ul className="space-y-2.5 mb-6">
              {['First listing FREE', '5 photos included', 'Map pin placement', 'Contact info display', 'Active until sale ends'].map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-[#004E89]">
                  <Check size={16} weight="bold" className="text-[#4CB944] flex-shrink-0 mt-0.5" />
                  {i === 0 ? <span className="font-bold">{item}</span> : item}
                </li>
              ))}
            </ul>

            <div className="bg-[#F9F9F6] border-2 border-dashed border-[#5C7A99] rounded-xl p-3 mb-5">
              <div className="flex items-center gap-2 text-sm font-bold text-[#004E89] mb-1">
                <Camera size={14} weight="bold" /> Extra Photos
              </div>
              <p className="text-xs text-[#5C7A99]">$2 per additional 5 photos</p>
            </div>

            <button onClick={() => handleCheckout('listing')} className="btn-secondary w-full py-2.5 text-sm" data-testid="buy-listing-btn">
              Post a Sale — ${appliedDiscount ? calcPrice(listingPrice).toFixed(2) : listingPrice.toFixed(2)}
            </button>
          </div>

          {/* Monthly Pro */}
          <div className="pricing-card relative" data-testid="pricing-monthly">
            {trialEligible && (
              <div className="absolute -top-3 left-5">
                <span className="bg-[#4CB944] text-white text-[10px] font-bold px-3 py-1 rounded-full border-2 border-[#004E89] shadow-[2px_2px_0px_#004E89] uppercase tracking-wider">
                  1st Month Free
                </span>
              </div>
            )}
            <div className="w-12 h-12 rounded-xl bg-[#FF6B35] border-2 border-[#004E89] flex items-center justify-center mb-4">
              <Lightning size={24} weight="fill" className="text-white" />
            </div>
            <h2 className="text-xl font-black text-[#004E89] mb-1" style={{ fontFamily: 'Outfit' }}>Pro Monthly</h2>
            <div className="flex items-baseline gap-1 mb-1">
              {appliedDiscount ? (
                <>
                  <span className="text-3xl font-black text-[#FF6B35]" style={{ fontFamily: 'Outfit' }}>${calcPrice(monthlyPrice).toFixed(2)}</span>
                  <span className="text-lg text-[#5C7A99] line-through font-medium">${monthlyPrice}</span>
                </>
              ) : (
                <span className="text-3xl font-black text-[#FF6B35]" style={{ fontFamily: 'Outfit' }}>${monthlyPrice}</span>
              )}
              <span className="text-[#5C7A99] font-medium text-sm">/month</span>
            </div>
            {appliedDiscount && (
              <div className="text-xs font-bold text-[#4CB944] mb-2">{appliedDiscount.percent_off}% off applied</div>
            )}

            <ul className="space-y-2.5 mb-6 mt-4">
              {['First month FREE', 'Unlimited listings', 'Unlimited photos', 'Post past sales history', 'All features included', 'Cancel anytime'].map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-[#004E89]">
                  <Star size={16} weight="fill" className="text-[#FFD166] flex-shrink-0 mt-0.5" />
                  {i === 0 ? <span className="font-bold">{item}</span> : item}
                </li>
              ))}
            </ul>

            {user?.referral_discount_credits > 0 && !isPremium && (
              <div className="bg-[#FFD166] bg-opacity-30 border-2 border-[#FF6B35] rounded-xl p-3 mb-4" data-testid="referral-discount-banner">
                <div className="flex items-center gap-2">
                  <Gift size={16} weight="fill" className="text-[#FF6B35] flex-shrink-0" />
                  <span className="text-xs font-bold text-[#004E89]">
                    ${user.referral_discount_credits} referral discount will be applied!
                  </span>
                </div>
              </div>
            )}

            {isPremium ? (
              <div className="bg-[#4CB944] text-white text-center py-2.5 rounded-xl border-2 border-[#004E89] font-bold text-sm shadow-[4px_4px_0px_#004E89]" data-testid="already-pro-badge">
                You are Pro!
              </div>
            ) : trialEligible ? (
              <button onClick={handleStartTrial} disabled={startingTrial}
                className="btn-primary w-full py-2.5 text-sm bg-[#4CB944] hover:bg-[#3da036]"
                data-testid="start-free-trial-btn"
              >
                {startingTrial ? 'Activating...' : 'Start Free Month'}
              </button>
            ) : (
              <button onClick={() => handleCheckout('subscription')} className="btn-primary w-full py-2.5 text-sm" data-testid="buy-monthly-btn">
                Go Monthly — ${appliedDiscount ? calcPrice(monthlyPrice).toFixed(2) : monthlyPrice.toFixed(2)}
              </button>
            )}
          </div>

          {/* Yearly Pro */}
          <div className="pricing-card featured relative" data-testid="pricing-yearly">
            <div className="absolute -top-3 right-5">
              <span className="bg-[#FF6B35] text-white text-[10px] font-bold px-3 py-1 rounded-full border-2 border-[#004E89] shadow-[2px_2px_0px_#004E89] uppercase tracking-wider">
                Save 20%
              </span>
            </div>

            <div className="w-12 h-12 rounded-xl bg-[#004E89] border-2 border-[#004E89] flex items-center justify-center mb-4">
              <CalendarBlank size={24} weight="fill" className="text-[#FFD166]" />
            </div>
            <h2 className="text-xl font-black text-[#004E89] mb-1" style={{ fontFamily: 'Outfit' }}>Pro Yearly</h2>
            <div className="flex items-baseline gap-1 mb-1">
              {appliedDiscount ? (
                <>
                  <span className="text-3xl font-black text-[#FF6B35]" style={{ fontFamily: 'Outfit' }}>${calcPrice(yearlyPrice).toFixed(2)}</span>
                  <span className="text-lg text-[#5C7A99] line-through font-medium">${yearlyPrice}</span>
                </>
              ) : (
                <span className="text-3xl font-black text-[#FF6B35]" style={{ fontFamily: 'Outfit' }}>${yearlyPrice}</span>
              )}
              <span className="text-[#5C7A99] font-medium text-sm">/year</span>
            </div>
            <div className="text-xs text-[#5C7A99] mb-1">
              {appliedDiscount ? (
                <span className="font-bold text-[#4CB944]">{appliedDiscount.percent_off}% off applied — ${(calcPrice(yearlyPrice) / 12).toFixed(2)}/mo effective</span>
              ) : (
                <span>$40/mo effective — <span className="font-bold text-[#FF6B35]">save $120/year</span></span>
              )}
            </div>

            <ul className="space-y-2.5 mb-6 mt-4">
              {['Everything in Monthly', 'First month FREE trial available', 'Unlimited listings & photos', '20% savings vs monthly', 'Full year of access', 'Cancel anytime'].map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-[#004E89]">
                  <Star size={16} weight="fill" className="text-[#FF6B35] flex-shrink-0 mt-0.5" />
                  {i === 1 ? <span className="font-bold">{item}</span> : item}
                </li>
              ))}
            </ul>

            {isPremium ? (
              <div className="bg-[#4CB944] text-white text-center py-2.5 rounded-xl border-2 border-[#004E89] font-bold text-sm shadow-[4px_4px_0px_#004E89]" data-testid="already-pro-yearly-badge">
                You are Pro!
              </div>
            ) : (
              <button onClick={() => handleCheckout('yearly_subscription')} className="btn-primary w-full py-2.5 text-sm flex items-center justify-center gap-2" data-testid="buy-yearly-btn">
                Go Yearly — ${appliedDiscount ? calcPrice(yearlyPrice).toFixed(2) : yearlyPrice.toFixed(2)}
                <ArrowRight size={16} weight="bold" />
              </button>
            )}
          </div>
        </div>

        {/* Vendor Events Section */}
        <div className="mt-10" data-testid="vendor-events-section">
          <div className="text-center mb-6">
            <h2 className="text-2xl sm:text-3xl font-black text-[#004E89]" style={{ fontFamily: 'Outfit' }}>
              Vendor Events
            </h2>
            <p className="text-[#5C7A99] mt-1">Craft Fairs, Food Vendors, Carnivals & More</p>
          </div>
          <div className="max-w-lg mx-auto">
            <div className="pricing-card relative border-[#7209B7] shadow-[6px_6px_0px_#7209B7]" data-testid="pricing-vendor">
              <div className="absolute -top-3 right-5">
                <span className="text-white text-[10px] font-bold px-3 py-1 rounded-full border-2 border-[#004E89] shadow-[2px_2px_0px_#004E89] uppercase tracking-wider" style={{ background: 'linear-gradient(135deg, #7209B7, #00B4D8)' }}>
                  Unlimited Photos
                </span>
              </div>
              <div className="w-12 h-12 rounded-xl border-2 border-[#004E89] flex items-center justify-center mb-4" style={{ background: 'linear-gradient(135deg, #7209B7, #00B4D8)' }}>
                <Storefront size={24} weight="fill" className="text-white" />
              </div>
              <h2 className="text-xl font-black text-[#004E89] mb-1" style={{ fontFamily: 'Outfit' }}>Vendor Event Listing</h2>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-3xl font-black text-[#7209B7]" style={{ fontFamily: 'Outfit' }}>$25</span>
                <span className="text-[#5C7A99] font-medium text-sm">/event</span>
              </div>
              <p className="text-xs text-[#5C7A99] mb-4">Perfect for craft fairs, food trucks, carnivals & vendor events</p>

              <ul className="space-y-2.5 mb-6">
                {[
                  { text: 'Unlimited photos included', bold: true },
                  { text: 'Map pin placement' },
                  { text: 'Full event details' },
                  { text: 'Contact info display' },
                  { text: 'Active until event ends' },
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[#004E89]">
                    <Check size={16} weight="bold" className="text-[#7209B7] flex-shrink-0 mt-0.5" />
                    {item.bold ? <span className="font-bold">{item.text}</span> : item.text}
                  </li>
                ))}
              </ul>

              <div className="bg-[#F9F9F6] border-2 border-dashed border-[#7209B7] rounded-xl p-3 mb-5 flex items-center gap-3">
                <VideoCamera size={22} weight="duotone" className="text-[#7209B7] flex-shrink-0" />
                <div>
                  <div className="text-sm font-bold text-[#004E89]">Add Videos — $2.00 each</div>
                  <p className="text-xs text-[#5C7A99]">Showcase your event with video walkthroughs</p>
                </div>
              </div>

              <button onClick={() => navigate('/create')} className="w-full py-2.5 text-sm font-bold rounded-xl border-2 border-[#004E89] shadow-[4px_4px_0px_#004E89] text-white transition-all hover:-translate-y-0.5" style={{ background: 'linear-gradient(135deg, #7209B7, #00B4D8)' }} data-testid="post-vendor-event-btn">
                Post a Vendor Event — $25.00
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
